import { isNumber } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Select, SliderInput } from '@meitu/saas-ui';
import { formatColor } from '@meitu/saas-colors';
import { parseStrokeDasharray } from '@meitu/saas-editor-core/es/utils/file-kit';
import { parseStrokeDasharrayToRender } from '@meitu/saas-editor-core/es/utils/vector-kit';
import { VectorType, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { DASHARRAY_LIST } from '../../config';
import { VectorPanelContext } from '../../context';
import { type StrokeStyleOption } from '../../types';
import { Tracker } from '../../utils';
import './style.less';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';

type ListenedProps = Pick<
    IVectorLayerProps,
    | 'vectorType'
    | 'stroke'
    | 'strokeWidth'
    | 'strokeLinecap'
    | 'strokeHidden'
    | 'strokeGradient'
    | 'strokeDasharray'
    | 'naturalWidth'
    | 'naturalHeight'
> & {
    layerWidth: number;
    layerHeight: number;
};

const { Option } = Select;

const FALLBACK_PROPS: ListenedProps = {
    layerWidth: 0,
    layerHeight: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    vectorType: VectorType.Rect,
    stroke: '',
    strokeWidth: 0,
    strokeLinecap: '',
    strokeHidden: false,
    strokeGradient: null,
    strokeDasharray: '',
};

const DEFAULT_STROKE_COLOR = '#000000ff';
const DEFAULT_STROKE_WIDTH = 1;

export const StrokeInspector: React.FC = () => {
    const { layerId, recordId, isIntl } = useContext(VectorPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return FALLBACK_PROPS;

        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as IVectorLayerProps;
        const {
            vectorType,
            stroke,
            strokeWidth,
            strokeLinecap,
            strokeHidden,
            strokeGradient,
            strokeDasharray,
            naturalWidth,
            naturalHeight,
        } = layerData;

        return {
            vectorType,
            layerWidth,
            layerHeight,
            naturalWidth,
            naturalHeight,
            stroke,
            strokeWidth,
            strokeLinecap,
            strokeHidden,
            strokeGradient,
            strokeDasharray,
        };
    }) as ListenedProps;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const {
        layerWidth,
        layerHeight,
        naturalWidth,
        naturalHeight,
        vectorType,
        stroke,
        strokeWidth,
        strokeLinecap,
        strokeHidden,
        strokeGradient,
        strokeDasharray,
    } = props;

    const strokeColor = strokeGradient ? (formatColor(strokeGradient) as string) : stroke;
    const maxValue = Math.ceil(Math.min(layerWidth, layerHeight) / 2);

    const [strokeStyles, setStrokeStyles] = useState<StrokeStyleOption[]>(DASHARRAY_LIST);

    const add = () => {
        if (strokeColor) return;
        editor
            .getInstance(VectorLayer)
            .addStroke(layerId, { color: DEFAULT_STROKE_COLOR, width: DEFAULT_STROKE_WIDTH });

        Tracker.addStroke({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const remove = () => {
        editor.getInstance(VectorLayer).removeStroke(layerId);

        Tracker.removeStroke({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const setVisibility = (flag: boolean) => {
        editor.getInstance(VectorLayer).setStrokeVisibility(layerId, flag);

        Tracker.updateStrokeProps({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const setStrokeColor = (color: string, options = { writeHistory: true }) => {
        editor.getInstance(VectorLayer).setStrokeColor(layerId, color, options);

        if (options.writeHistory) {
            Tracker.updateStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                isColor: true,
            });
        }
    };

    const setStrokeStyle = (dasharray: string, linecap: string) => {
        editor.getInstance(VectorLayer).setStrokeStyle(layerId, { dasharray, linecap });

        Tracker.updateStrokeProps({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const setStrokeWidth = (value: number, options = { writeHistory: true }) => {
        if (!isNumber(value)) return;
        editor.getInstance(VectorLayer).setStrokeWidth(layerId, value, options);

        if (options.writeHistory) {
            Tracker.updateStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openStrokeColorPicker({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    useEffect(() => {
        const exist = DASHARRAY_LIST.find((item) => item.value === strokeDasharray && item.linecap === strokeLinecap);

        if (!exist) {
            setStrokeStyles((prev) => [...prev, { value: strokeDasharray, linecap: strokeLinecap }]);
        }
    }, [strokeDasharray, strokeLinecap]);

    return (
        <EffectPropsContainer
            icon="BorderBold"
            title={$t((v) => v.panel.vectorPanel.stroke)}
            showAdd={stroke === ''}
            visible={!strokeHidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!strokeHidden)}
        >
            {strokeColor ? (
                <div className="vector-stroke-inspector">
                    <EffectPropsGroup disabled={strokeHidden}>
                        {/* 描边颜色 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.color)} align>
                            <ColorGroup
                                isGradient
                                popupTriggerSize={{ width: 78, height: 32 }}
                                color={strokeColor}
                                setColor={(value) => setStrokeColor(value, { writeHistory: false })}
                                setColorAfter={(value, _, { isBrandColor }) => {
                                    setStrokeColor(value, { writeHistory: true });
                                    if (isBrandColor) {
                                        window._stat_track?.('hb_layers_func_use', {
                                            func_id: '9_107',
                                        });
                                    }
                                }}
                                popupOverlayClassName="vector-panel-color-picker"
                                onOpenChange={handleColorPickerOpenChange}
                                onToggleBizTab={(bizTab) => {
                                    if (bizTab === 'team') {
                                        window._stat_track?.('hb_layers_func_click', {
                                            func_id: '9_107',
                                        });
                                    }
                                }}
                            />
                        </LabelAction>

                        {/* 描边样式 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.style)} align>
                            <div className="vector-stroke-inspector__dasharray">
                                <Select
                                    value={strokeDasharray}
                                    className="vector-stroke-inspector__select"
                                    suffixIcon={
                                        <IconFont
                                            name="ChevronDownBlack"
                                            fontSize={10}
                                            color="var(--content-input-lable)"
                                        />
                                    }
                                    onChange={(_: string, option: any) => setStrokeStyle(option.value, option.linecap)}
                                >
                                    {strokeStyles.map((item, index) => {
                                        return (
                                            <Option key={index} linecap={item.linecap} value={item.value}>
                                                <div className="vector-stroke-inspector__option">
                                                    {item.linecap === 'round' ? (
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                width: 148,
                                                                marginLeft: 4,
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <svg
                                                                width="150"
                                                                height="26"
                                                                viewBox="0 0 150 26"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill="none"
                                                                    d="M13 13 L140 13"
                                                                    stroke="#666666"
                                                                    strokeWidth={2}
                                                                    strokeDasharray={
                                                                        parseStrokeDasharray(
                                                                            item.value,
                                                                            2,
                                                                            true,
                                                                        ) as string
                                                                    }
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <svg
                                                            width="140"
                                                            height="26"
                                                            viewBox="0 0 140 26"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fill="none"
                                                                d="M16 13L212 13"
                                                                stroke="#666666"
                                                                strokeWidth={2}
                                                                strokeDasharray={parseStrokeDasharrayToRender(
                                                                    item.value,
                                                                    2,
                                                                )}
                                                                strokeLinecap="butt"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </div>
                        </LabelAction>

                        {/* 描边宽度 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.thickness)} align>
                            <div className="vector-stroke-inspector__width" style={{ width: isIntl ? '100%' : 174 }}>
                                <SliderInput
                                    showStepBtn
                                    type="horizontal"
                                    value={strokeWidth}
                                    max={maxValue}
                                    min={0}
                                    onChange={(value) => setStrokeWidth(value, { writeHistory: false })}
                                    onAfterChange={(value) => setStrokeWidth(value, { writeHistory: true })}
                                />
                            </div>
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
